import { useState, useEffect, useMemo } from "react";
import { vendorFieldsMapping } from '../../../../Constants/Data';
import { updateInformation, updateProductInformation } from '../../../API_Handler';
import { useNavigate } from 'react-router-dom';

const VendorModal = ({ selectedVendor, backgroundColor, vendorProducts }) => {
    const navigate = useNavigate();
    const [vendor, setVendor] = useState(selectedVendor);
    const [editedVendor, setEditedVendor] = useState(selectedVendor);
    const [expandedVendor, setExpandedVendor] = useState(false);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [confirmation, setConfirmation] = useState(false);
    const [save, setSave] = useState(false);
    const [cancel, setCancel] = useState(false);
    const [changedFields, setChangedFields] = useState({});
    const [statusConfirmation, setStatusConfirmation] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const hasChanges = useMemo(() => {
        if (!vendor || !editedVendor) return false;
        return JSON.stringify(vendor) !== JSON.stringify(editedVendor);
    }, [vendor, editedVendor]);

    const handleExpandVendor = async () => {
        if (vendor.application_viewed === false && expandedVendor === false) {
            setLoading(true);
            try {
                let response = await updateInformation(selectedVendor.id, { application_viewed: true });

                if (response?.errors && response.errors.length > 0) {
                    const error = response.errors[0];
                    if (error.errorType === "StringDataRightTruncation") {
                        alert("Error: One or more fields exceed the maximum allowed length. Please shorten your input and try again.");

                    } else {
                        alert(`Error: ${error.message || "Failed to update vendor information."}`);
                    }
                    setError(true);
                } else {
                    setVendor(response);
                    setEditedVendor(response);
                    setError(false);
                }
            } catch (error) {
                alert("Error: An unexpected error occurred while updating vendor information.");
                setError(true);
            } finally {
                setLoading(false);
            }
        }
        setExpandedVendor(!expandedVendor);
    };

    const markAsUnread = async () => {
        if (vendor.application_viewed === true) {
            setLoading(true);
            try {
                let response = await updateInformation(selectedVendor.id, { application_viewed: false });

                if (response?.errors && response.errors.length > 0) {
                    const error = response.errors[0];
                    if (error.errorType === "StringDataRightTruncation") {
                        alert("Error: One or more fields exceed the maximum allowed length. Please shorten your input and try again.");
                    } else {
                        alert(`Error: ${error.message || "Failed to update vendor information."}`);
                    }
                    setError(true);
                } else {
                    setVendor(response);
                    setEditedVendor(response);
                    setError(false);
                }
            } catch (error) {
                alert("Error: An unexpected error occurred while updating vendor information.");
                setError(true);
            } finally {
                setLoading(false);
            }
        }
    };

    const requestStatusChange = (status) => {
        setPendingStatus(status);
        setStatusConfirmation(true);
    };

    const confirmStatusChange = async () => {
        setStatusConfirmation(false);
        if (pendingStatus === "ACTIVATED") {
            await handleChangeVendorActiveStatus(true);
        } else if (pendingStatus === "DEACTIVATED") {
            await handleChangeVendorActiveStatus(false);
        } else if (pendingStatus === "APPROVED") {
            await handleChangeVendorActiveStatus(true);
            await handleChangeVendorApprovalStatus("APPROVED");
        } else {
            await handleChangeVendorApprovalStatus(pendingStatus);
        }
        setPendingStatus(null);
    };

    const cancelStatusChange = () => {
        setStatusConfirmation(false);
        setPendingStatus(null);
    };

    const getStatusConfirmationMessage = () => {
        switch (pendingStatus) {
            case "APPROVED":
                return "Are you sure you want to approve this vendor? They will be able to publish products on the marketplace.";
            case "REJECTED":
                return "Are you sure you want to reject this vendor? They will not be able to access the marketplace.";
            case "DEACTIVATED":
                return "Are you sure you want to deactivate this vendor? All their published products will be unpublished.";
            case "ACTIVATED":
                return "Are you sure you want to activate this vendor? They will be able to access the marketplace again.";
            default:
                return "Are you sure you want to change this vendor's status?";
        }
    };

    const handleChangeVendorApprovalStatus = async (status) => {
        setLoading(true);
        try {
            const currentTime = new Date().toISOString();
            let response = await updateInformation(selectedVendor.id, {
                approval_status: status,
                last_status_update: currentTime
            });

            if (response?.errors && response.errors.length > 0) {
                const error = response.errors[0];
                if (error.errorType === "StringDataRightTruncation") {
                    alert("Error: One or more fields exceed the maximum allowed length. Please shorten your input and try again.");
                } else {
                    alert(`Error: ${error.message || "Failed to update vendor information."}`);
                }
                setError(true);
            } else {
                setChangedFields({});
                setVendor(response);
                setEditedVendor(response);
                setError(false);
            }
        } catch (error) {
            alert("Error: An unexpected error occurred while updating vendor information.");
            setError(true);
        } finally {
            setLoading(false);
        }
    }

    const handleChangeVendorActiveStatus = async (status) => {
        setLoading(true);
        try {
            if (vendorProducts && vendorProducts.length > 0) {
                for (let i = 0; i < vendorProducts.length; i++) {
                    if (vendorProducts[i].is_published) {
                        const productUpdate = await updateProductInformation(vendorProducts[i].id, { is_published: false });
                        if (productUpdate === null) {
                            setError(true);
                            return;
                        }
                        vendorProducts[i] = productUpdate;
                    }
                }
            }

            const currentTime = new Date().toISOString();
            let response = await updateInformation(selectedVendor.id, { is_active: status, last_status_update: currentTime });

            if (response?.errors && response.errors.length > 0) {
                const error = response.errors[0];
                if (error.errorType === "StringDataRightTruncation") {
                    alert("Error: One or more fields exceed the maximum allowed length. Please shorten your input and try again.");
                } else {
                    alert(`Error: ${error.message || "Failed to update vendor information."}`);
                }
                setError(true);
            } else {
                setChangedFields({});
                setVendor(response);
                setEditedVendor(response);
                setError(false);
            }
        } catch (error) {
            alert("Error: An unexpected error occurred while updating vendor information.");
            setError(true);
        } finally {
            setLoading(false);
        }
    }

    const getChangedFields = () => {
        const changes = {};
        for (const key in vendor) {
            if (vendor[key] !== editedVendor[key]) {
                changes[key] = editedVendor[key];
            }
        }
        return changes;
    }

    const handleSave = () => {
        const changes = getChangedFields();
        setChangedFields(changes);
        if (Object.keys(changes).length > 0) {
            setConfirmation(true);
            setSave(true);
        }
    }

    const handleCancel = () => {
        const changes = getChangedFields();
        setChangedFields(changes);
        if (Object.keys(changes).length > 0) {
            setConfirmation(true);
            setCancel(true);
        } else {
            setIsEditing(false);
        }
    }

    const handleConfirm = async () => {
        setLoading(true);
        try {
            let changedFields = getChangedFields();
            let response = await updateInformation(selectedVendor.id, changedFields);

            if (response?.errors && response.errors.length > 0) {
                const error = response.errors[0];
                if (error.errorType === "StringDataRightTruncation") {
                    alert("Error: One or more fields exceed the maximum allowed length. Please shorten your input and try again.");
                } else {
                    alert(`Error: ${error.message || "Failed to update vendor information."}`);
                }
                setError(true);
                setEditedVendor(vendor)
            } else {
                setVendor(response);
                setEditedVendor(response);
                setError(false);
                setIsEditing(false);
            }
        } catch (error) {
            alert("Error: An unexpected error occurred while updating vendor information.");
            setError(true);
        } finally {
            setSave(false);
            setConfirmation(false);
            setLoading(false);
        }
    }

    const handleDiscard = () => {
        setEditedVendor(vendor);
        setConfirmation(false);
        setSave(false);
        setCancel(false);
        setIsEditing(false);
    }

    return (
        <div className={`w-full h-fit ${expandedVendor ? "bg-[#fff6e7]" : backgroundColor} flex flex-col border-[1px] ${expandedVendor ? "border-charmOrange" : "border-transparent"}`}>
            <div className={`w-full ${expandedVendor ? "bg-[#fff6e7]" : backgroundColor} px-[16px] py-[1px] flex flex-row justify-between items-center ${expandedVendor ? "border-b-[1px] border-[#a0a0a0]" : ""}   hover:bg-[#fff6e7]`}>
                <p className="min-w-[60px] max-w-[60px] pr-[16px] font-proximanova text-[18px] text-[#505050] font-[500] border-r-[1px] border-[#a0a0a0] mb-0 cursor-pointer"
                    onClick={handleExpandVendor} >
                    {vendor.id}</p>

                <div className="w-full pl-[16px] flex items-center gap-2 cursor-pointer" onClick={handleExpandVendor}>
                    {vendor.application_viewed === false && (
                        <span className="px-3 py-1 bg-[#76da24] text-white text-[14px] font-proximanova font-[700] rounded-full">
                            NEW
                        </span>
                    )}
                    <p className={`font-proximanova text-[18px] text-[#505050] text-left mb-0 ${vendor.application_viewed === false ? 'font-[700]' : 'font-[500]'}`}>
                        {vendor.company_name}
                    </p>
                </div>

                <div className="flex flex-row justify-between">
                    <div className="w-[160px] flex flex-row items-center border-l-[1px] pl-[16px] border-[#a0a0a0] gap-[8px]">
                        <div className={`w-[8px] h-[8px] rounded-full ${vendor.is_active === true ? 'bg-[#76da24]' : 'bg-[#fd5764]'} `}></div>
                        <p className="w-fit font-proximanova text-[18px] text-[#505050] font-[500] mb-0">
                            {vendor.is_active ? "ACTIVE" : "DEACTIVATED"}
                        </p>
                    </div>
                    <div className="w-[140px] flex flex-row items-center border-l-[1px] pl-[16px] border-[#a0a0a0] gap-[8px]">
                        <div className={`w-[8px] h-[8px] rounded-full ${vendor.approval_status === "PENDING" ? 'bg-[#bbbbbb]' : vendor.approval_status === "APPROVED" ? 'bg-[#76da24]' : vendor.approval_status === "REJECTED" ? 'bg-[#fd5764]' : ''} `}></div>
                        <p className="w-fit font-proximanova text-[18px] text-[#505050] font-[500] mb-0">{vendor.approval_status}</p>
                    </div>

                    <div className="min-w-[80px] max-w-[80px] border-l-[1px] pl-[16px] border-[#a0a0a0] flex flex-row items-center justify-center">
                        {expandedVendor === false ?
                            (
                                <button className="w-fit flex flex-row items-center justify-center font-proximanova text-charmOrange text-[18px] hover:text-charmHover border-b-[1px] border-charmOrange hover:border-charmHover mb-0"
                                    onClick={handleExpandVendor}
                                >
                                    Expand
                                </button>
                            ) : (
                                <button className="w-fit flex flex-row items-center justify-center font-proximanova text-charmOrange text-[18px] hover:text-charmHover border-b-[1px] border-charmOrange hover:border-charmHover mb-0"
                                    onClick={handleExpandVendor}
                                >
                                    Collapse
                                </button>
                            )}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="w-full h-fit flex items-center justify-center">
                    {error ? (
                        <div className="w-full py-[16px] flex items-center justify-center">
                            <p className="text-[18px] text-red font-proximanova font-[400] mb-0">Error updating vendor, please refresh to make sure you are logged in and try again.</p>
                        </div>
                    ) : (
                        <div className="w-full py-[16px] flex items-center justify-center">
                            <span
                                className="spinner-border spinner-border-sm mr-2"
                                role="status"
                                aria-hidden="true"
                            ></span>
                        </div>
                    )}
                </div>
            ) : statusConfirmation ? (
                <div className="w-full h-full flex flex-col overflow-y-auto pb-[16px] px-[16px]">
                    <p className="mt-[16px] text-[18px] text-[#505050] font-proximanova font-[400] mb-0">
                        {getStatusConfirmationMessage()}
                    </p>

                    <div className="mt-[16px] flex flex-row items-center justify-center gap-4">
                        <button
                            className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#76da24] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full"
                            onClick={confirmStatusChange}
                        >
                            Confirm
                        </button>

                        <button
                            className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#fd5764] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full"
                            onClick={cancelStatusChange}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : confirmation ? (
                <div className="w-full h-full flex flex-col overflow-y-auto pb-[16px] px-[16px]">
                    {save ? (
                        <p className="mt-[16px] text-[18px] text-[#fd5764] font-proximanova font-[400] mb-0">Are you sure you want to save these changes?</p>
                    ) : cancel ? (
                        <p className="mt-[16px] text-[18px] text-[#fd5764] font-proximanova font-[400] mb-0">Are you sure you want to discard these changes?</p>
                    ) : ""}

                    <div className="w-full mt-[16px] flex flex-col gap-[8px]">
                        {Object.keys(changedFields).map((key) => (
                            <div className="flex flex-col border-[1px] border-[#999999] p-[8px]" key={key}>
                                <p className="w-full text-[18px] font-proximanova mb-0">{vendorFieldsMapping[key]}: </p>
                                <p className="w-full text-[18px] font-proximanova mb-0">{vendor[key]}</p>
                                <div className="w-full flex flex-row">
                                    <p className="ml-[16px] text-left text-[14px] text-[#999999] font-proximanova mb-0">▼</p>
                                </div>
                                <p className="w-full text-[18px] font-proximanova mb-0">{editedVendor[key]}</p>
                            </div>
                        ))}
                    </div>

                    {save ? (
                        <div className="mt-[16px] flex flex-row items-center justify-center gap-4">
                            <button className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#76da24] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full"
                                onClick={() => {
                                    handleConfirm();
                                }}>Save Changes</button>

                            <button className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#fd5764] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full"
                                onClick={() => {
                                    handleDiscard();
                                }}>Cancel</button>
                        </div>
                    ) : cancel ? (
                        <div className="mt-[16px] flex flex-row items-center justify-center gap-4">
                            <button className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#76da24] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full"
                                onClick={() => {
                                    handleDiscard();
                                }}>Discard Changes</button>

                            <button className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#fd5764] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full"
                                onClick={() => {
                                    setConfirmation(false);
                                    setCancel(false);
                                }}>Keep Editing</button>
                        </div>
                    ) : ""}
                </div>

            ) : (
                expandedVendor &&
                <div className="w-full h-full mt-[16px] px-[16px] flex flex-col overflow-y-auto pb-[16px]">
                    <div className="w-full flex flex-row justify-between gap-4">
                        <div className="flex flex-row gap-4">
                            {vendor.application_viewed === true && (
                                <button
                                    className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#bbbbbb] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full"
                                    onClick={() => markAsUnread()}
                                >
                                    Mark as Unread
                                </button>
                            )}
                        </div>

                        <div className="flex flex-row gap-4">
                            {isEditing ? (
                                <>
                                    <button className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#76da24] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full" onClick={handleSave}>
                                        Save Changes
                                    </button>
                                    <button className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#fd5764] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full" onClick={handleCancel}>
                                        Cancel
                                    </button>
                                </>
                            ) : hasChanges ? (
                                <>
                                    <button className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#76da24] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full" onClick={handleSave}>
                                        Save Changes
                                    </button>
                                    <button className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#fd5764] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full" onClick={handleCancel}>
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-charmOrange hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full"
                                        onClick={() => setIsEditing(true)}
                                    >
                                        Edit
                                    </button>
                                    {vendor.is_active === true ? (
                                        <button
                                            className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#fd5764] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                            onClick={() => requestStatusChange("DEACTIVATED")}
                                            disabled={loading}
                                        >
                                            {loading ? 'Processing...' : 'Deactivate'}
                                        </button>
                                    ) :
                                        (vendor.is_active === false && vendor.approval_status !== "PENDING") ? (
                                            <button
                                                className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#76da24] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                                onClick={() => requestStatusChange("ACTIVATED")}
                                                disabled={loading}
                                            >
                                                {loading ? 'Processing...' : 'Activate'}
                                            </button>
                                        ) :
                                            (
                                                <>
                                                    <button
                                                        className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#76da24] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                                        onClick={() => requestStatusChange("APPROVED")}
                                                        disabled={loading}
                                                    >
                                                        {loading ? 'Processing...' : 'Approve'}
                                                    </button>
                                                    <button
                                                        className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#fd5764] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                                        onClick={() => requestStatusChange("REJECTED")}
                                                        disabled={loading}
                                                    >
                                                        {loading ? 'Processing...' : 'Reject'}
                                                    </button>
                                                </>
                                            )}
                                </>
                            )}
                        </div>
                    </div>

                    {selectedVendor && (
                        <div className="mt-[4px] w-full flex flex-col">
                            <h3 className="w-full text-[20px] font-proximanova font-[600] text-[#505050] mb-0">Vendor Information</h3>
                            <div className="w-full flex flex-col lg:flex-row lg:gap-[12px]">
                                <div className="w-full lg:w-1/3 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Vendor Name</label>
                                    <input
                                        type="text"
                                        className={`w-full p-1 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                        value={editedVendor.company_name}
                                        onChange={(e) => {
                                            setEditedVendor({ ...editedVendor, company_name: e.target.value });
                                        }}
                                        disabled={!isEditing}
                                    />
                                </div>

                                <div className="w-full lg:w-1/3 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Vendor Address</label>
                                    <input
                                        className={`w-full p-1 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                        value={editedVendor.address}
                                        onChange={(e) => {
                                            setEditedVendor({ ...editedVendor, address: e.target.value });
                                        }}
                                        disabled={!isEditing}
                                    />
                                </div>

                                <div className="w-full lg:w-1/3 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Vendor Website</label>
                                    <input
                                        className={`w-full p-1 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                        value={editedVendor.website}
                                        onChange={(e) => {
                                            setEditedVendor({ ...editedVendor, website: e.target.value });
                                        }}
                                        disabled={!isEditing}
                                    />
                                </div>
                            </div>

                            <label className="mt-[8px] w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-1 ">Vendor Description</label>
                            <textarea
                                className={`w-full h-48 block p-1 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                value={editedVendor.vendor_bio}
                                onChange={(e) => {
                                    setEditedVendor({ ...editedVendor, vendor_bio: e.target.value });
                                }}
                                disabled={!isEditing}
                            />

                            <div className="mt-[8px] w-full flex flex-col lg:flex-row lg:gap-[12px]">
                                <div className="w-full lg:w-1/3 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Year Founded</label>
                                    <input
                                        className={`w-full p-1 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                        value={editedVendor.year_founded}
                                        onChange={(e) => {
                                            setEditedVendor({ ...editedVendor, year_founded: e.target.value });
                                        }}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div className="w-full lg:w-1/3 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Number of Employees</label>
                                    <select
                                        value={editedVendor.number_of_employees}
                                        onChange={(e) => setEditedVendor({ ...editedVendor, number_of_employees: e.target.value })}
                                        className={`w-full p-1 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                        disabled={!isEditing}
                                    >
                                        <option value="" disabled>Select Number of Employees</option>
                                        <option value="0-50">0-50</option>
                                        <option value="51-100">51-100</option>
                                        <option value="100-500">100-500</option>
                                        <option value="500+">500+</option>
                                    </select>
                                </div>
                                <div className="w-full lg:w-1/3 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Revenue</label>
                                    <select
                                        value={editedVendor.revenue}
                                        onChange={(e) => setEditedVendor({ ...editedVendor, revenue: e.target.value })}
                                        className={`w-full p-1 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                        disabled={!isEditing}
                                    >
                                        <option value="">Select Revenue Range</option>
                                        <option value="0-149999">$0 - $149,999</option>
                                        <option value="150000-299999">$150,000 - $299,999</option>
                                        <option value="300000-449999">$300,000 - $449,999</option>
                                        <option value="450000-599999">$450,000 - $599,999</option>
                                        <option value="600000-749999">$600,000 - $749,999</option>
                                        <option value="750000-899999">$750,000 - $899,999</option>
                                        <option value="900000-1049999">$900,000 - $1,049,999</option>
                                        <option value="1050000-1199999">$1,050,000 - $1,199,999</option>
                                        <option value="1200000-1349999">$1,200,000 - $1,349,999</option>
                                        <option value="1350000-1499999">$1,350,000 - $1,499,999</option>
                                        <option value="1500000+">$1,500,000+</option>
                                    </select>
                                </div>
                            </div>
                            <label className="mt-[8px] w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Service Specialties</label>
                            <textarea
                                className={`w-full h-36 block p-1 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                maxlength="300"
                                value={editedVendor.service_specialties}
                                onChange={(e) => setEditedVendor({ ...editedVendor, service_specialties: e.target.value })}
                                disabled={!isEditing}
                            />
                            <label className="mt-[8px] w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Service Description</label>
                            <textarea
                                className={`w-full h-36 block p-1 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                maxlength="1200"
                                value={editedVendor.service_description}
                                onChange={(e) => setEditedVendor({ ...editedVendor, service_description: e.target.value })}
                                disabled={!isEditing}
                            />
                            <label className="mt-[8px] w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Goal</label>
                            <textarea
                                className={`w-full h-36 block p-1 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                maxlength="1200"
                                value={editedVendor.serve_plan}
                                onChange={(e) => setEditedVendor({ ...editedVendor, serve_plan: e.target.value })}
                                disabled={!isEditing}
                            />

                            <div className="mt-[8px] w-full flex flex-col lg:flex-row lg:gap-[12px]">
                                <div className="w-full lg:w-1/2 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">CHIC</label>
                                    <select
                                        value={editedVendor.is_chic_participant === null ? false : editedVendor.is_chic_participant}
                                        onChange={(e) => setEditedVendor({ ...editedVendor, is_chic_participant: e.target.value === 'true' })}
                                        className={`w-full p-2 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                        disabled={!isEditing}
                                    >
                                        <option value={true}>IS CharmHealth Innovation Challenge Participant</option>
                                        <option value={false}>IS NOT CharmHealth Innovation Challenge Participant</option>
                                    </select>
                                </div>

                                <div className="w-full lg:w-1/2 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">API Interest</label>
                                    <select
                                        value={editedVendor.has_api_interest === null ? false : editedVendor.has_api_interest}
                                        onChange={(e) => setEditedVendor({ ...editedVendor, has_api_interest: e.target.value })}
                                        className={`w-full p-2 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                        disabled={!isEditing}
                                    >
                                        <option value={true}>IS Interested in APIs</option>
                                        <option value={false}>IS NOT Interested in APIs</option>
                                    </select>
                                </div>
                            </div>

                            <div className="mt-[8px] w-full flex flex-col lg:flex-row lg:gap-[12px]">
                                <div className="w-full lg:w-1/2 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Title</label>
                                    <input
                                        className={`w-full p-1 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                        value={editedVendor.title}
                                        onChange={(e) => setEditedVendor({ ...editedVendor, title: e.target.value })}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div className="w-full lg:w-1/2 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Contact Name</label>
                                    <input
                                        className={`w-full p-1 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                        value={editedVendor.contact_name}
                                        onChange={(e) => setEditedVendor({ ...editedVendor, contact_name: e.target.value })}
                                        disabled={!isEditing}
                                    />
                                </div>
                            </div>
                            <div className="mt-[8px] w-full flex flex-col lg:flex-row lg:gap-[12px]">
                                <div className="w-full lg:w-1/2 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Contact Email</label>
                                    <p className="w-full p-1 h-[34px] bg-[#DCDCDC] font-proximanova text-[#6D6D6D] text-[18px] border-[1px] border-[#e1e4e8] mb-0">
                                        {editedVendor.contact_email}
                                    </p>
                                </div>
                                <div className="w-full lg:w-1/2 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Contact Phone</label>
                                    <input
                                        className={`w-full p-1 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                        value={editedVendor.contact_phone_number}
                                        onChange={(e) => setEditedVendor({ ...editedVendor, contact_phone_number: e.target.value })}
                                        disabled={!isEditing}
                                    />
                                </div>
                            </div>

                            <label className="mt-[8px] w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Internal Comments</label>
                            <textarea
                                className={`w-full h-48 block p-1 font-proximanova text-[18px] focus:border-charmOrange focus:outline-none border-[1px] border-[#e1e4e8] ${!isEditing ? 'bg-[#ECECEC] text-[#6D6D6D] cursor-not-allowed' : 'bg-white'}`}
                                value={editedVendor.vendor_comments}
                                onChange={(e) => setEditedVendor({ ...editedVendor, vendor_comments: e.target.value })}
                                disabled={!isEditing}
                            />

                            <div className="mt-[8px] w-full flex flex-col lg:flex-row lg:gap-[12px]">
                                <div className="w-full lg:w-1/3 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Last Updated</label>
                                    <p className="w-full p-1 h-[34px] bg-[#DCDCDC] font-proximanova text-[#6D6D6D] text-[18px] border-[1px] border-[#e1e4e8] mb-0">
                                        {new Date(vendor.updated_at).toLocaleString()}
                                    </p>
                                </div>

                                <div className="w-full lg:w-1/3 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Last Status Update</label>
                                    <p className="w-full p-1 h-[34px] bg-[#DCDCDC] font-proximanova text-[#6D6D6D] text-[18px] border-[1px] border-[#e1e4e8] mb-0">
                                        {vendor.last_status_update ? new Date(vendor.last_status_update).toLocaleString() : 'N/A'}
                                    </p>
                                </div>

                                <div className="w-full lg:w-1/3 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Date Approved</label>
                                    <p className="w-full p-1 h-[34px] bg-[#DCDCDC] font-proximanova text-[#6D6D6D] text-[18px] border-[1px] border-[#e1e4e8] mb-0">
                                        {vendor.date_approved ? new Date(vendor.date_approved).toLocaleString() : 'N/A'}
                                    </p>
                                </div>

                                <div className="w-full lg:w-1/3 flex flex-col">
                                    <label className="w-full text-left font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Date Registered</label>
                                    <p className="w-full p-1 h-[34px] bg-[#DCDCDC] font-proximanova text-[#6D6D6D] text-[18px] border-[1px] border-[#e1e4e8] mb-0">
                                        {new Date(vendor.date_registered).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )
                    }
                    <h3 className="mt-[8px] w-full text-[20px] font-proximanova font-[600] text-[#505050] mb-0">{selectedVendor.company_name}'s Products</h3>

                    {vendorProducts && vendorProducts.length > 0 ?
                        <div className="w-full flex flex-col">
                            <div className="hidden lg:flex w-full flex-row">
                                <p className="lg:w-full font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Product Name</p>
                                <p className="lg:w-1/5 font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Status</p>
                                <p className="min-w-[108px] font-proximanova font-[600] text-[18px] text-[#505050] mb-0">Actions</p>
                            </div>
                            {vendorProducts.map((product) => {
                                return (
                                    <div key={product.id} className="w-full flex flex-row">
                                        <p className="w-full text-[18px] font-proximanova mb-0">{product.name}</p>
                                        <p className="w-1/5 text-start text-[18px] font-proximanova mb-0">{product.is_published ? "Published" : "Unpublished"}</p>
                                        <div className="min-w-[108px] flex justify-start">
                                            <button
                                                className="w-fit flex flex-row items-center justify-center font-proximanova text-charmOrange text-[18px] hover:text-charmHover border-b-[1px] border-charmOrange hover:border-charmHover mb-0"
                                                onClick={() => {
                                                    navigate(`/admins/profile/products/${product.id}`);
                                                    window.location.reload();
                                                }}
                                            >
                                                View Product
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        :
                        <div className="w-full flex flex-col">
                            <p className="w-full text-[18px] font-proximanova mb-0">No products available for this vendor</p>
                        </div>
                    }

                    <div className="mt-[16px] w-full flex flex-row justify-end gap-4">
                        {isEditing ? (
                            <>
                                <button className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#76da24] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full" onClick={handleSave}>
                                    Save Changes
                                </button>
                                <button className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#fd5764] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full" onClick={handleCancel}>
                                    Cancel
                                </button>
                            </>
                        ) : hasChanges ? (
                            <>
                                <button className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#76da24] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full" onClick={handleSave}>
                                    Save Changes
                                </button>
                                <button className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#fd5764] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full" onClick={handleCancel}>
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-charmOrange hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full"
                                    onClick={() => setIsEditing(true)}
                                >
                                    Edit
                                </button>
                                {vendor.is_active === true ? (
                                    <button
                                        className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#fd5764] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={() => requestStatusChange("DEACTIVATED")}
                                        disabled={loading}
                                    >
                                        {loading ? 'Processing...' : 'Deactivate'}
                                    </button>
                                ) :
                                    (vendor.is_active === false && vendor.approval_status !== "PENDING") ? (
                                        <button
                                            className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#76da24] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                            onClick={() => requestStatusChange("ACTIVATED")}
                                            disabled={loading}
                                        >
                                            {loading ? 'Processing...' : 'Activate'}
                                        </button>
                                    ) :
                                        (
                                            <>
                                                <button
                                                    className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#76da24] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                                    onClick={() => requestStatusChange("APPROVED")}
                                                    disabled={loading}
                                                >
                                                    {loading ? 'Processing...' : 'Approve'}
                                                </button>
                                                <button
                                                    className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-[#fd5764] hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                                    onClick={() => requestStatusChange("REJECTED")}
                                                    disabled={loading}
                                                >
                                                    {loading ? 'Processing...' : 'Reject'}
                                                </button>
                                            </>
                                        )}
                            </>
                        )}
                    </div>
                </div >
            )}
        </div>
    );
}

export default VendorModal;